import { Badge, IconButton, Tooltip } from "@mui/material";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import type { NsIntegration, NsMap, TargetType } from "@/api/netsuiteMap";

// Status colors carried over from the original map; brand navy and orange
// come from the app theme (primary / secondary).
export const TONES = {
  auto: { fg: "#1D7A6A", bg: "#DDF1EC" },
  review: { fg: "#3563A6", bg: "#E1EAF7" },
  manual: { fg: "#5F6973", bg: "#ECEFF2" },
  watch: { fg: "#7A5BA6", bg: "#ECE5F5" },
  warn: { fg: "#8A5800", bg: "#FBEFD5" },
  accent: { fg: "#8A3B07", bg: "#FBE7D7" },
};

export const MONO = "ui-monospace, \"SF Mono\", Menlo, Consolas, monospace";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// "New since May 2026": the purchasing, AP and AR work this map was built to show.
export const NEW_SINCE = "2026-05";

export const isNew = (i: NsIntegration) => i.since !== "pre" && i.since >= NEW_SINCE;

export function sinceLabel(since: string): string {
  if (since === "pre") return "Earlier";
  if (since.length === 4) return since;
  const [y, m] = since.split("-");
  return `${MONTHS[Number(m) - 1]} ${y}`;
}

export const writes = (i: NsIntegration) => i.direction === "W" || i.direction === "RW";

export const targetId = (type: TargetType, key: string) => `${type}:${key}`;

export interface Target {
  type: TargetType;
  key: string;
}

// What a comment is about, for the drawer and the All feedback list.
export function targetLabel(map: NsMap, type: TargetType, key: string): string {
  if (type === "general") return "The map as a whole";
  if (type === "integration") return map.integrations.find((i) => i.id === key)?.name ?? "Removed integration";
  if (type === "record") {
    const row = map.records.flatMap((f) => f.rows).find((r) => r.id === key);
    return row ? `${row.name} (who creates it)` : "Removed record row";
  }
  return map.open_items.find((o) => o.id === key)?.title ?? "Removed open item";
}

export function CommentButton({ count, label, onClick }: { count: number; label: string; onClick: () => void }) {
  return (
    <Tooltip title={count ? `${count} comment${count === 1 ? "" : "s"} on ${label}` : `Comment on ${label}`}>
      <IconButton size="small" onClick={onClick} aria-label={`Comments on ${label}`} sx={{ color: count ? "secondary.main" : "text.disabled" }}>
        <Badge badgeContent={count} color="secondary" max={99}>
          <ChatBubbleOutlineIcon sx={{ fontSize: 18 }} />
        </Badge>
      </IconButton>
    </Tooltip>
  );
}
